<template>
  <Confirm ref="confirmRef" :max-width="maxWidth">
    <template v-slot:card-title="{ close }">
      <v-card-title class="alert-dialog__header">
        <div class="alert-dialog__header-inner">
          <span class="text-h6 alert-dialog__title">
            {{ step === 1 ? $t('title_step1') : $t('title_step2') }}
          </span>
          <v-btn depressed icon class="alert-dialog__close" @click="close">
            <v-icon size="18">close</v-icon>
          </v-btn>
        </div>
      </v-card-title>
    </template>
    <template v-slot:content>
      <div v-if="step === 1" class="limits-dialog__panel limits-dialog__panel--step1">
        <p class="limits-dialog__lead limits-dialog__lead--center">
          {{ $t('lead_step1') }}
        </p>
        <button type="button" class="limits-dialog__link" @click="goLearnMore">
          {{ $t('btn_learn_more') }}
        </button>
      </div>
      <div v-else class="limits-dialog__panel limits-dialog__panel--step2">
        <p class="limits-dialog__intro">
          {{ $t('intro_step2') }}
        </p>
        <p class="limits-dialog__factors-heading">{{ $t('factors_heading') }}</p>
        <ul class="limits-dialog__list">
          <li
            v-for="(factor, index) in $t('factors')"
            :key="index"
            class="limits-dialog__list-item">
            {{ factor }}
          </li>
        </ul>
        <p class="limits-dialog__outro">
          {{ $t('outro_step2') }}
        </p>
      </div>
    </template>
    <template v-slot:footer="{ close }">
      <template v-if="step === 1">
        <span></span>
      </template>
      <template v-else-if="showUpgradeButton">
        <div class="limits-dialog__actions">
          <v-btn
            color="primary"
            depressed
            class="limits-dialog__upgrade text-none"
            min-width="187"
            :to="{ name: 'Upgrade' }"
            @click="close">
            {{ $t('btn_upgrade') }}
          </v-btn>
        </div>
      </template>
      <template v-else>
        <span></span>
      </template>
    </template>
  </Confirm>
</template>

<script>
import { mapState } from 'vuex'
import Confirm from '@/components/Confirm.vue'

export default {
  name: 'LimitsAlert',
  components: {
    Confirm
  },
  i18n: {
    sharedMessages: {
      en: {
        title_step1: "You've reached your current account limits.",
        title_step2: 'Account Limits',
        lead_step1: 'Continue using the platform to unlock higher limits over time.',
        btn_learn_more: 'Learn More',
        intro_step2: 'Your account limits are designed to grow as you continue using the platform.',
        factors_heading: 'Limits may increase based on factors such as:',
        factors: [
          'Account activity and usage history',
          'Successful payment history',
          'Good account standing'
        ],
        outro_step2:
          'Continue using the platform to build your account history and unlock higher limits over time. You can also send a request to upgrade your account to help increase your limits faster.',
        btn_upgrade: 'Upgrade Account'
      },
      zh_CN: {
        title_step1: '您已达到当前账户限额',
        title_step2: '账户限额',
        lead_step1: '继续使用平台，限额将随时间逐步提高。',
        btn_learn_more: '了解更多',
        intro_step2: '您的账户限额会随您持续使用平台而增长。',
        factors_heading: '限额可能根据以下因素提高：',
        factors: ['账户活动和使用记录', '成功付款历史', '账户整体状态良好'],
        outro_step2: '请正常使用平台以逐步提高限额。您也可以发送请求升级账户以更快提高限额。',
        btn_upgrade: '升级账户'
      }
    }
  },
  props: {
    maxWidth: {
      type: String,
      default: '600px'
    }
  },
  data() {
    return {
      step: 1
    }
  },
  computed: {
    ...mapState('client', ['upgradeInfo']),
    showUpgradeButton() {
      return !this.upgradeInfo || this.upgradeInfo.count < 1
    }
  },
  methods: {
    goLearnMore() {
      this.step = 2
    },
    open(data) {
      this.step = 1
      if (this.$refs.confirmRef) {
        this.$refs.confirmRef.open(data)
        this.$SDK.track('account_limits_viewed')
      }
    },
    close() {
      if (this.$refs.confirmRef) {
        this.$refs.confirmRef.close()
      }
    }
  }
}
</script>

<style scoped>
.alert-dialog__header-inner {
  width: 100%;
  position: relative;
  text-align: center;
}

.alert-dialog__title {
  display: inline-block;
  padding: 0 28px;
}

.alert-dialog__close {
  position: absolute;
  top: -10px;
  right: -15px;
  margin: 0;
}

.limits-dialog__panel--step1 {
  text-align: center;
}

.limits-dialog__lead {
  margin: 0;
  font-size: 14px;
  font-weight: 300;
  line-height: 1.5;
  color: #000;
}

.limits-dialog__lead--center {
  text-align: center;
}

.limits-dialog__link {
  margin-top: 20px;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  font-family: inherit;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.35;
  color: #332ee8;
  text-decoration: underline;
}

.limits-dialog__panel--step2 {
  text-align: left;
}

.limits-dialog__intro {
  margin: 0;
  font-size: 14px;
  font-weight: 300;
  line-height: 1.5;
  color: #000;
}

.limits-dialog__factors-heading {
  margin: 20px 0 0;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
  color: #000;
}

.limits-dialog__list {
  margin: 12px 0 0;
  padding: 0;
  list-style: none;
}

.limits-dialog__list-item {
  position: relative;
  margin: 0;
  padding: 0 0 0 16px;
  font-size: 14px;
  font-weight: 300;
  line-height: 1.5;
  color: #000;
}

.limits-dialog__list-item + .limits-dialog__list-item {
  margin-top: 4px;
}

.limits-dialog__list-item::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0.55em;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #332ee8;
  transform: translateY(-50%);
}

.limits-dialog__outro {
  margin: 16px 0 0;
  font-size: 14px;
  font-weight: 300;
  line-height: 1.5;
  color: #000;
}

.limits-dialog__actions {
  display: flex;
  justify-content: center;
  width: 100%;
}
</style>
